import type {
  CompanyShopperControls,
  YuzuBlock,
  YuzuTemplatePage,
  YuzuTemplateSchema,
} from '@giftie/yuzu-integration'
import type { GreetingCardFormat } from '@giftie/shared/greeting-cards'
import { parseYuzuTemplateSchema } from '@giftie/yuzu-integration'
import { findYuzuBlock, removeYuzuBlock, updateYuzuBlock } from '~/utils/yuzuBlockTree'
import {
  canMutateYuzuBlock,
  createYuzuBlockMap,
  isYuzuImageReplaceable,
} from '~/utils/yuzuBlockPolicies'
import {
  freezeYuzuSessionText,
  type YuzuTextResolution,
} from '~/utils/yuzuSessionText'
import { clearUntouchedYuzuHandwriting } from '~/utils/yuzuHandwriting'
import { sortYuzuPages } from '~/utils/yuzuTemplateLayout'

export type CompanyShopperState = CompanyShopperControls & {
  variables?: Record<string, string | number | null>
}

export type YuzuBuilderState = {
  companyShopper?: CompanyShopperState
  templateId: string
  templateVersionId: string
  shop: string
  shopifyVariantId: string
  cardFormat: GreetingCardFormat
  safeAreaMm: number
  sessionId?: string
  creationIntentId: string
  restoredFromSession: boolean
  sourceSchema: YuzuTemplateSchema
  paperSize: YuzuTemplateSchema['paperSize']
  pages: YuzuTemplatePage[]
  activePageIndex: number
  selectedBlockId?: string
}
type YuzuTextResolver = () => Promise<void>
const textResolvers = new Map<string, YuzuTextResolver>()

/** Owns isolated raw Yuzu schema state for the v2 greeting-card builder. */
export function useYuzuBuilder() {
  const state = useState<YuzuBuilderState | null>('yuzu-builder', () => null)
  const initializationError = useState<string | null>('yuzu-builder:error', () => null)
  const textResolutions = useState<Record<string, YuzuTextResolution>>(
    'yuzu-builder:text-resolutions',
    () => ({}),
  )
  const blockMap = computed(() => createYuzuBlockMap(state.value?.pages ?? []))
  const activePage = computed(() => state.value?.pages[state.value.activePageIndex] ?? null)
  const selectedBlock = computed(() => {
    if (!state.value?.selectedBlockId) return null
    return findYuzuBlock(state.value.pages.flatMap((page) => page.blocks), state.value.selectedBlockId)
  })

  /** Accepts a validated schema as the sole v2 template state. */
  function initialize(input: {
    templateId: string
    templateVersionId: string
    shop: string
    shopifyVariantId: string
    cardFormat: GreetingCardFormat
    safeAreaMm: number
    sessionId?: string
    restoredFromSession?: boolean
    schema: unknown
    companyShopper?: CompanyShopperState
    sourceSchema?: unknown
  }) {
    const schema = parseYuzuTemplateSchema(input.schema)
    const sourceSchema = input.sourceSchema
      ? parseYuzuTemplateSchema(input.sourceSchema)
      : schema
    textResolutions.value = {}
    state.value = {
      companyShopper: input.companyShopper,
      templateId: input.templateId,
      templateVersionId: input.templateVersionId,
      shop: input.shop,
      shopifyVariantId: input.shopifyVariantId,
      cardFormat: input.cardFormat,
      safeAreaMm: input.safeAreaMm,
      sessionId: input.sessionId,
      creationIntentId: createCreationIntentId(),
      restoredFromSession: input.restoredFromSession === true,
      sourceSchema,
      paperSize: schema.paperSize,
      pages: sortYuzuPages(schema.pages),
      activePageIndex: 0,
    }
    initializationError.value = null
  }

  function canMutate(
    block: YuzuBlock,
    action: Parameters<typeof canMutateYuzuBlock>[2],
  ) {
    const shopper = state.value?.companyShopper
    if (shopper)
      return (
        (action === 'select' || action === 'edit') &&
        block.id === shopper.messageId
      )
    return canMutateYuzuBlock(block, blockMap.value, action)
  }

  /** Selects a block only when its effective locked policy allows selection. */
  function selectBlock(blockId?: string) {
    if (!state.value) return false
    if (!blockId) {
      state.value.selectedBlockId = undefined
      return true
    }

    const block = blockMap.value.get(blockId)
    if (!block || !canMutate(block, 'select')) return false
    state.value.selectedBlockId = blockId
    return true
  }

  /** Runs a supported mutation through the central locked-policy guard. */
  function mutateBlock(
    blockId: string,
    action: Parameters<typeof canMutateYuzuBlock>[2],
    update: (block: YuzuBlock) => YuzuBlock,
  ) {
    if (!state.value) return false
    const block = blockMap.value.get(blockId)
    if (!block || !canMutate(block, action)) return false

    state.value.pages = updateYuzuBlock(state.value.pages, blockId, update)
    return true
  }

  /** Replaces text content without changing the surrounding Yuzu block. */
  function updateText(blockId: string, html: string) {
    return mutateBlock(blockId, 'edit', (block) => ({
      ...block,
      children: [html, ...block.children.slice(1)],
    }))
  }

  /** Replaces an image source without changing unknown image attributes. */
  function updateImage(blockId: string, src: string) {
    const block = blockMap.value.get(blockId)
    if (!block || !isYuzuImageReplaceable(block)) return false
    return mutateBlock(blockId, 'edit', (block) => ({
      ...block,
      attributes: { ...block.attributes, src },
    }))
  }

  /** Adds one shopper-created block directly to a raw Yuzu page. */
  function addBlock(pageId: string, block: YuzuBlock) {
    if (!state.value || state.value.companyShopper)
      return false
    const pageExists = state.value.pages.some((page) => page.id === pageId)
    if (!pageExists) return false

    state.value.pages = state.value.pages.map((page) => (
      page.id === pageId ? { ...page, blocks: [...page.blocks, block] } : page
    ))
    return true
  }

  /** Removes an unlocked shopper-created block from the raw Yuzu schema. */
  function removeBlock(blockId: string) {
    if (!state.value) return false
    const block = blockMap.value.get(blockId)
    if (!block || !canMutate(block, 'delete')) return false
    state.value.pages = removeYuzuBlock(state.value.pages, blockId)
    if (state.value.selectedBlockId === blockId) state.value.selectedBlockId = undefined
    return true
  }

  /** Exposes the current raw Yuzu schema for v2 persistence. */
  function getSchema(): YuzuTemplateSchema | null {
    if (!state.value) return null
    return {
      ...state.value.sourceSchema,
      paperSize: state.value.paperSize,
      pages: state.value.pages,
    }
  }

  /** Registers one mounted text box so session saves can await its final fit. */
  function registerTextResolver(blockId: string, resolver: YuzuTextResolver) {
    textResolvers.set(blockId, resolver)
    return () => {
      if (textResolvers.get(blockId) === resolver) textResolvers.delete(blockId)
    }
  }

  /** Records the latest customer-visible size without changing the live schema. */
  function setTextResolution(blockId: string, resolution: YuzuTextResolution) {
    textResolutions.value = {
      ...textResolutions.value,
      [blockId]: resolution,
    }
  }

  /** Commits and recalculates mounted text, then returns a render-stable session snapshot. */
  async function getSessionSchema(): Promise<YuzuTemplateSchema | null> {
    if (!state.value) return null
    if (state.value.companyShopper) return getCompanySchema()

    await Promise.all([...textResolvers.values()].map((resolve) => resolve()))
    const schema = getSchema()
    if (!schema) return null
    return clearUntouchedYuzuHandwriting(freezeYuzuSessionText(schema, textResolutions.value), state.value.sourceSchema)
  }

  /** Preserve fixed blocks and geometry while saving the message at its displayed font size. */
  async function getCompanySchema() {
    const id = state.value?.companyShopper
      ?.messageId
    if (!id) return getSchema()
    await textResolvers.get(id)?.()
    const schema = getSchema()
    if (schema && state.value) {
      const cleared = clearUntouchedYuzuHandwriting(schema, state.value.sourceSchema)
      const message = findYuzuBlock(cleared.pages.flatMap(page => page.blocks), id)
      if (message?.type === 'handwriting') {
        schema.pages = updateYuzuBlock(schema.pages, id, () => message)
        // Core fits handwriting; browser text measurements must not change its settings.
        return schema
      }
    }
    const resolution = textResolutions.value[id]
    if (!schema || !resolution) return schema
    return {
      ...schema,
      pages: updateYuzuBlock(schema.pages, id, (block) => ({
        ...block,
        styles: {
          ...block.styles,
          fontSize: `${Number(resolution.fontSizePt.toFixed(3))}pt`,
        },
        attributes: { ...block.attributes, autoResize: false },
      })),
    }
  }

  return {
    state,
    activePage,
    selectedBlock,
    blockMap,
    initializationError,
    initialize,
    selectBlock,
    mutateBlock,
    updateText,
    updateImage,
    addBlock,
    removeBlock,
    getSchema,
    getSessionSchema,
    registerTextResolver,
    setTextResolution,
  }
}

/** Keep session creation IDs valid on HTTP development origins too. */
function createCreationIntentId(): string {
  if (typeof globalThis.crypto.randomUUID === 'function')
    return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
